import { useEffect, useRef, useState } from "react";
import {
  Autocomplete,
  Box,
  IconButton,
  MenuItem,
  Popover,
  Select,
  Switch,
  TextField,
} from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import type { FormRow } from "../types";

const NATIVE = "__native__";

// Decimal-typed quantity values (JSON type ["string","number"]) stay strings to
// preserve precision; only a purely numeric value schema is coerced to a number.
function coerceQuantityValue(raw: string, quantitySchema: any): string | number {
  const t = quantitySchema?.properties?.value?.type;
  const types = (Array.isArray(t) ? t : [t]).filter((x: unknown) => x && x !== "null");
  const numericOnly =
    types.length === 1 && (types[0] === "number" || types[0] === "integer");
  if (!numericOnly) return raw;
  const n = Number(raw);
  return Number.isNaN(n) ? raw : n;
}

const inputSx = {
  "& .MuiOutlinedInput-notchedOutline": { borderColor: "#000" },
  "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#000" },
  "& .Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#000" },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#000" },
  "& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline": { borderColor: "#000" },
  "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#000" },
  "& .MuiInputLabel-root": { color: "#000" },
  "& .MuiInputLabel-root.Mui-focused": { color: "#000" },
};

export interface ValueCellEditorProps {
  anchorEl: HTMLElement;
  row: FormRow;
  onCommit: (value: unknown) => void;
  onSwitchBranch?: (branchIndex: number) => void;
  onCancel: () => void;
}

export const ValueCellEditor = ({
  anchorEl,
  row,
  onCommit,
  onSwitchBranch,
  onCancel,
}: ValueCellEditorProps) => {
  const [pending, setPending] = useState<unknown>(row.value ?? "");
  const inputRef = useRef<HTMLInputElement>(null);

  const initialQ =
    row.value && typeof row.value === "object" && !Array.isArray(row.value)
      ? (row.value as { value?: unknown; unit?: unknown })
      : {};
  const [qValue, setQValue] = useState<string>(initialQ.value != null ? String(initialQ.value) : "");
  const [qUnit, setQUnit] = useState<string>(initialQ.unit != null ? String(initialQ.unit) : NATIVE);

  useEffect(() => {
    setPending(row.value ?? (row.fieldType === "boolean" ? false : ""));
    const q =
      row.value && typeof row.value === "object" && !Array.isArray(row.value)
        ? (row.value as { value?: unknown; unit?: unknown })
        : {};
    setQValue(q.value != null ? String(q.value) : "");
    setQUnit(q.unit != null ? String(q.unit) : NATIVE);
  }, [row.id, row.value, row.fieldType]);

  useEffect(() => {
    const t = setTimeout(() => inputRef.current?.focus(), 50);
    return () => clearTimeout(t);
  }, []);

  const commit = () => {
    if (row.fieldType === "quantity") {
      const trimmed = qValue.trim();
      if (trimmed === "") {
        onCommit(undefined);
        return;
      }
      const next: Record<string, unknown> = {
        value: coerceQuantityValue(trimmed, row.schema),
      };
      if (qUnit && qUnit !== NATIVE) next.unit = qUnit;
      onCommit(next);
      return;
    }
    if (row.fieldType === "oneOf" && onSwitchBranch) {
      onSwitchBranch(Number(pending));
      return;
    }
    if (row.fieldType === "number" || row.fieldType === "integer") {
      if (pending === "" || pending === null || pending === undefined) {
        onCommit(undefined);
        return;
      }
      const n = Number(pending);
      onCommit(Number.isNaN(n) ? undefined : n);
      return;
    }
    if (row.fieldType === "string" || row.fieldType === "enum") {
      if (pending === "" || pending === null || pending === undefined) {
        onCommit(undefined);
        return;
      }
    }
    onCommit(pending);
  };

  const handleKey = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && row.fieldType !== "boolean") {
      e.preventDefault();
      commit();
    } else if (e.key === "Escape") {
      e.preventDefault();
      onCancel();
    }
  };

  let input: React.ReactNode;
  if (row.fieldType === "boolean") {
    input = (
      <Switch
        checked={Boolean(pending)}
        onChange={(_, checked) => setPending(checked)}
      />
    );
  } else if (row.fieldType === "enum" && row.enumOptions) {
    input = (
      <Autocomplete
        size="small"
        sx={{ minWidth: 240, ...inputSx }}
        value={
          row.enumOptions.find((o) => o.value === pending) ??
          (pending !== "" && pending != null
            ? { value: pending, label: String(pending) }
            : null)
        }
        options={row.enumOptions}
        getOptionLabel={(o) => (typeof o === "string" ? o : o.label)}
        onChange={(_, v) => setPending(v?.value ?? "")}
        renderInput={(p) => <TextField {...p} inputRef={inputRef} label={row.label} />}
        isOptionEqualToValue={(o, v) => o.value === v.value}
      />
    );
  } else if (row.fieldType === "oneOf" && row.oneOfBranches) {
    input = (
      <Select
        size="small"
        sx={{ minWidth: 240, ...inputSx }}
        value={String(pending ?? row.activeOneOfIndex ?? 0)}
        onChange={(e) => setPending(Number(e.target.value))}
      >
        {row.oneOfBranches.map((b) => (
          <MenuItem key={b.index} value={String(b.index)}>
            {b.title}
          </MenuItem>
        ))}
      </Select>
    );
  } else if (row.fieldType === "quantity") {
    const unitOptions = [
      { value: NATIVE, label: "Native (tokens)" },
      ...(row.enumOptions ?? []).map((o) => ({ value: String(o.value), label: o.label })),
    ];
    input = (
      <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
        <TextField
          size="small"
          sx={{ minWidth: 160, ...inputSx }}
          value={qValue}
          onChange={(e) => setQValue(e.target.value)}
          label={row.label}
          inputRef={inputRef}
          inputProps={{ inputMode: "decimal" }}
        />
        <Select
          size="small"
          sx={{ minWidth: 150, ...inputSx }}
          value={qUnit}
          onChange={(e) => setQUnit(String(e.target.value))}
        >
          {unitOptions.map((o) => (
            <MenuItem key={o.value} value={o.value}>
              {o.label}
            </MenuItem>
          ))}
        </Select>
      </Box>
    );
  } else {
    input = (
      <TextField
        size="small"
        sx={{ minWidth: 240, ...inputSx }}
        type={row.isSecret ? "password" : row.fieldType === "number" || row.fieldType === "integer" ? "number" : "text"}
        value={pending ?? ""}
        onChange={(e) => setPending(e.target.value)}
        label={row.label}
        inputRef={inputRef}
      />
    );
  }

  // Pre-seed pending for oneOf to current active branch
  useEffect(() => {
    if (row.fieldType === "oneOf") {
      setPending(row.activeOneOfIndex ?? 0);
    }
  }, [row.fieldType, row.activeOneOfIndex]);

  return (
    <Popover
      open={true}
      anchorEl={anchorEl}
      onClose={onCancel}
      anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      transformOrigin={{ vertical: "top", horizontal: "left" }}
      slotProps={{ paper: { sx: { p: 2 } } }}
      onKeyDown={handleKey}
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        {input}
        {row.errorMessage && (
          <Box sx={{ color: "#e57373", fontSize: 12 }}>{row.errorMessage}</Box>
        )}
        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 0.5 }}>
          <IconButton size="small" aria-label="Cancel" onClick={onCancel}>
            <CloseIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" aria-label="Confirm" onClick={commit} sx={{ color: "#000" }}>
            <CheckIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>
    </Popover>
  );
};
